import { ICompanyInfo, IDashboardLicenses, ILCHttpResponse, IRiskHRRiskLevel, ITitleValueString } from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import {
  IDashboardAlertsPercentageMenWomenRequest,
  IDashboardRisksByCitiesRequest,
  IDashboardRisksPerTopicRequest,
} from '../../interfaces/dashboard.interface'
import { RootState } from '../redux/store'
import { ChartData } from '../ui/components/charts/customComponents/helperCharts'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

export const dashboardApi = createApi({
  reducerPath: 'dashboardApi',
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/dashboard`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  endpoints: (builder) => ({
    getRisksPerTopic: builder.query<ChartData[], IDashboardRisksPerTopicRequest>({
      query: () => ({
        url: '/risks-per-topic',
        method: 'GET',
      }),
    }),
    // getDashboardData: builder.query<IDashboardCharts, IDashboardRisksPerTopicRequest>({
    //   query: () => ({
    //     url: '/dashboard-data',
    //     method: 'GET',
    //   }),
    // }),
    getRisksPerTopicDepartment: builder.query<ChartData[], ChartData>({
      query: () => ({
        url: '/risks-per-department',
        method: 'GET',
      }),
    }),
    getAlertsPercentageMenWomen: builder.query<ChartData[], IDashboardAlertsPercentageMenWomenRequest>({
      query: () => ({
        url: '/alerts-percentage-men-women',
        method: 'GET',
      }),
    }),
    postAlertsMenVsWomenHighest: builder.query<ILCHttpResponse<ChartData[]>, { startDate?: string; endDate?: string }>({
      query: ({ startDate, endDate }) => ({
        url: '/alertsMenVsWomenHighest',
        method: 'POST',
        body: {
          startDate,
          endDate,
        },
      }),
    }),
    getLicenses: builder.query<IDashboardLicenses, void>({
      query: () => ({
        url: '/licenses',
      }),
      keepUnusedDataFor: 10, // 10 seconds cache instead of default 60 seconds
    }),
    getTotalMenVsWomen: builder.query<ChartData[], void>({
      query: () => ({
        url: '/total/men-women',
      }),
    }),
    getRisksByCountries: builder.query<ChartData[], void>({
      query: () => ({
        url: '/risks-by-countries',
      }),
    }),
    getRisksByCities: builder.query<ChartData[], IDashboardRisksByCitiesRequest>({
      query: ({ country }) => ({
        url: `/risks-by-cities/${country}`,
      }),
    }),
    getDetailedCompanyInformation: builder.query<ICompanyInfo, void>({
      query: () => ({
        url: '/detailed-company-information',
      }),
    }),
    getEvaluationStatus: builder.query<ChartData[], void>({
      query: () => ({
        url: '/evaluation-status',
      }),
    }),
    getRiskHrAverageState: builder.query<ITitleValueString[], void>({
      query: () => ({
        url: '/riskhr-average-state',
      }),
    }),
    getRiskHrRiskLevel: builder.query<IRiskHRRiskLevel, void>({
      query: () => ({
        url: '/riskhr-risk-level',
      }),
    }),
    getRiskHrTopicRisk: builder.query<IRiskHRRiskLevel, void>({
      query: () => ({
        url: '/riskhr-topic-risk',
      }),
    }),
    postGeneralEmployeeStatusId: builder.query<ILCHttpResponse<ChartData[]>, { startDate?: string; endDate?: string }>({
      query: ({ startDate, endDate }) => ({
        url: '/employeeStatusId',
        method: 'POST',
        body: {
          startDate,
          endDate,
        },
      }),
    }),
    postGeneralEmpByCountry: builder.query<ILCHttpResponse<ChartData[]>, { startDate?: string; endDate?: string }>({
      query: ({ startDate, endDate }) => ({
        url: '/empByCountry',
        method: 'POST',
        body: {
          startDate,
          endDate,
        },
      }),
    }),
    postGeneralEmpByDepartment: builder.query<ILCHttpResponse<ChartData[]>, { startDate?: string; endDate?: string }>({
      query: ({ startDate, endDate }) => ({
        url: '/empByDepartment',
        method: 'POST',
        body: {
          startDate,
          endDate,
        },
      }),
    }),
    postGeneralEmpByPosition: builder.query<ILCHttpResponse<ChartData[]>, { startDate?: string; endDate?: string }>({
      query: ({ startDate, endDate }) => ({
        url: '/empByPosition',
        method: 'POST',
        body: {
          startDate,
          endDate,
        },
      }),
    }),
    postGeneralEmpByGender: builder.query<ILCHttpResponse<ChartData[]>, { startDate?: string; endDate?: string }>({
      query: ({ startDate, endDate }) => ({
        url: '/empByGender',
        method: 'POST',
        body: {
          startDate,
          endDate,
        },
      }),
    }),
    postGeneralEmpByGenderAgeRange: builder.query<ILCHttpResponse<ChartData[]>, { startDate?: string; endDate?: string }>({
      query: ({ startDate, endDate }) => ({
        url: '/EmpByGenderAgeRange',
        method: 'POST',
        body: {
          startDate,
          endDate,
        },
      }),
    }),
    postGeneralEmpByAgeRange: builder.query<ILCHttpResponse<ChartData[]>, { startDate?: string; endDate?: string }>({
      query: ({ startDate, endDate }) => ({
        url: '/EmpByAgeRange',
        method: 'POST',
        body: {
          startDate,
          endDate,
        },
      }),
    }),
    postGeneralEmpByOrganizationId: builder.query<ILCHttpResponse<ChartData[]>, { startDate?: string; endDate?: string }>({
      query: ({ startDate, endDate }) => ({
        url: '/EmpByOrganizationId',
        method: 'POST',
        body: {
          startDate,
          endDate,
        },
      }),
    }),
    postAlertsMenVsWomenByAge: builder.query<ILCHttpResponse<ChartData[]>, { startDate?: string; endDate?: string }>({
      query: ({ startDate, endDate }) => ({
        url: '/alertsMenVsWomenByAge',
        method: 'POST',
        body: {
          startDate,
          endDate,
        },
      }),
    }),
  }),
})

export const {
  useLazyGetRisksPerTopicQuery,
  useLazyGetAlertsPercentageMenWomenQuery,
  useLazyGetRisksPerTopicDepartmentQuery,
  useLazyGetLicensesQuery,
  useLazyGetTotalMenVsWomenQuery,
  useLazyGetRisksByCountriesQuery,
  useLazyGetRisksByCitiesQuery,
  useLazyGetDetailedCompanyInformationQuery,
  useLazyGetEvaluationStatusQuery,
  useLazyGetRiskHrAverageStateQuery,
  useLazyGetRiskHrRiskLevelQuery,
  useLazyGetRiskHrTopicRiskQuery,
  useLazyPostGeneralEmployeeStatusIdQuery,
  useLazyPostGeneralEmpByCountryQuery,
  useLazyPostGeneralEmpByDepartmentQuery,
  useLazyPostGeneralEmpByPositionQuery,
  useLazyPostGeneralEmpByGenderQuery,
  useLazyPostGeneralEmpByGenderAgeRangeQuery,
  useLazyPostGeneralEmpByAgeRangeQuery,
  useLazyPostGeneralEmpByOrganizationIdQuery,
  useLazyPostAlertsMenVsWomenByAgeQuery,
  useLazyPostAlertsMenVsWomenHighestQuery,
} = dashboardApi
